/**
 * SsoScimSection — SCIM 2.0 provisioning admin block, embedded
 * on /system/settings/sso below the SSO config card (ADR-024
 * Phase 3.C). Two sub-blocks:
 *
 * 1. Bearer-token CRUD (Phase 3.A): generate / show-once / revoke.
 *    GET status never returns plaintext — admin sees it only at
 *    generate-time.
 * 2. Group-mapping CRUD (Phase 3.B): admin declares "IdP group X
 *    = svdy department Y" or "IdP group X = svdy role R". Without
 *    a mapping the IdP's SCIM /Groups payloads are accepted but
 *    produce no svdy mutation.
 *
 * §13 chrome verbatim (1px borders, no shadows, size:small
 * buttons + borderRadius:1).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  GroupOutlined,
  KeyOutlined,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { ApiError, systemApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';
import { departmentsApi } from '../../../api/organization';
import { ConfirmDialog } from '../../../components/primitives';
import { activeLocale } from '../../../utils/datetime';
import type {
  ScimGroupMappingResponse,
  ScimGroupMappingUpsert,
  ScimTokenGenerateResponse,
  ScimTokenStatusResponse,
} from '../../../api/system';

interface DepartmentOption {
  id: string;
  name: string;
}
interface RoleOption {
  id: string;
  name: string;
}

const SsoScimSection: React.FC = () => {
  const { t } = useTranslation();
  // Token state
  const [tokenStatus, setTokenStatus] = useState<ScimTokenStatusResponse | null>(null);
  const [generated, setGenerated] = useState<ScimTokenGenerateResponse | null>(null);
  const [showOnce, setShowOnce] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenBusy, setTokenBusy] = useState(false);

  // Mappings + picker state
  const [mappings, setMappings] = useState<ScimGroupMappingResponse[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [mappingsLoading, setMappingsLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<ScimGroupMappingUpsert>({
    idp_group_id: '',
    idp_group_display_name: '',
    target_kind: 'department',
    target_id: '',
  });
  const [addBusy, setAddBusy] = useState(false);
  // Pending group-mapping delete — drives a ConfirmDialog instead of the
  // forbidden native window.confirm() (svdy-ui §12.6).
  const [pendingDeleteMapping, setPendingDeleteMapping] =
    useState<ScimGroupMappingResponse | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fetchToken = useCallback(async () => {
    setTokenLoading(true);
    try {
      const r = await systemApi.systemScimApi.getTokenStatus();
      setTokenStatus(r);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:ssoScim.errors.tokenStatusLoad'));
    } finally {
      setTokenLoading(false);
    }
  }, [t]);

  const fetchMappings = useCallback(async () => {
    setMappingsLoading(true);
    try {
      const [list, deps, rls] = await Promise.all([
        systemApi.systemScimApi.listGroupMappings(),
        departmentsApi.list().catch(() => []),
        systemApi.systemRolesApi.list().catch(() => []),
      ]);
      setMappings(list);
      setDepartments(
        Array.isArray(deps) ? deps.map((d: any) => ({ id: d.id, name: d.name })) : []
      );
      setRoles(
        Array.isArray(rls) ? rls.map((r: any) => ({ id: r.id, name: r.name })) : []
      );
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:ssoScim.errors.mappingsLoad'));
    } finally {
      setMappingsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchToken();
    fetchMappings();
  }, [fetchToken, fetchMappings]);

  // ---- Token actions ----

  const handleGenerate = async () => {
    setTokenBusy(true);
    setError(null);
    try {
      const result = await systemApi.systemScimApi.generateToken();
      setGenerated(result);
      setShowOnce(true);
      setTokenStatus({
        configured: true,
        token_prefix: result.token_prefix,
        created_at: result.created_at,
        last_used_at: null,
        last_rotated_at: null,
        request_count: 0,
      });
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:ssoScim.errors.generate'));
    } finally {
      setTokenBusy(false);
      setConfirmRotate(false);
    }
  };

  const handleRevoke = async () => {
    setTokenBusy(true);
    setError(null);
    try {
      await systemApi.systemScimApi.revokeToken();
      setTokenStatus({
        configured: false,
        token_prefix: null,
        created_at: null,
        last_used_at: null,
        last_rotated_at: null,
        request_count: 0,
      });
      setToast(t('system:ssoScim.tokenRevoked'));
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:ssoScim.errors.revoke'));
    } finally {
      setTokenBusy(false);
      setConfirmRevoke(false);
    }
  };

  const handleCopyToken = () => {
    if (generated?.token) {
      navigator.clipboard.writeText(generated.token).catch(() => undefined);
      setToast(t('system:ssoScim.tokenGenerated'));
    }
  };

  // ---- Mapping actions ----

  const handleAddMapping = async () => {
    if (!addForm.idp_group_id.trim() || !addForm.target_id) {
      setError(t('system:ssoScim.errors.missingFields'));
      return;
    }
    setAddBusy(true);
    setError(null);
    try {
      await systemApi.systemScimApi.createGroupMapping({
        ...addForm,
        idp_group_display_name: addForm.idp_group_display_name?.trim() || null,
      });
      setToast(t('system:ssoScim.tokenGenerated'));
      setAddOpen(false);
      setAddForm({
        idp_group_id: '',
        idp_group_display_name: '',
        target_kind: 'department',
        target_id: '',
      });
      await fetchMappings();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:ssoScim.errors.create'));
    } finally {
      setAddBusy(false);
    }
  };

  const confirmDeleteMapping = async () => {
    if (!pendingDeleteMapping) return;
    setError(null);
    try {
      await systemApi.systemScimApi.deleteGroupMapping(pendingDeleteMapping.id);
      setToast(t('system:ssoScim.tokenRevoked'));
      setPendingDeleteMapping(null);
      await fetchMappings();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:ssoScim.errors.delete'));
      throw e; // keep ConfirmDialog open for retry
    }
  };

  // ---- render ----

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: '100%',
        p: 3,
        mt: 3,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <KeyOutlined fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={600}>
          {t('system:ssoScim.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('system:ssoScim.subtitle')}{' '}
        <code style={{ marginLeft: 4 }}>{t('system:ssoScim.endpointPath')}</code>
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {toast ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setToast(null)}>
          {toast}
        </Alert>
      ) : null}

      {/* Token block */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('system:ssoScim.bearerToken')}
      </Typography>
      {tokenLoading ? (
        <CircularProgress size={20} sx={{ my: 2 }} />
      ) : tokenStatus?.configured ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack spacing={0.25}>
            <Typography variant="body2">
              {t('system:ssoScim.tokenPrefixLabel')}
              <code style={{ marginLeft: 4 }}>{tokenStatus.token_prefix}…</code>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tokenStatus.last_used_at
                ? `${t('system:ssoScim.lastUsedPrefix')} ${new Date(tokenStatus.last_used_at).toLocaleString(activeLocale())}`
                : t('system:ssoScim.notUsedYet')}
              {' · '}{t('system:ssoScim.requestsLabel')} <strong>{tokenStatus.request_count}</strong>
              {tokenStatus.last_rotated_at
                ? ` · ${t('system:ssoScim.lastRotatedPrefix')} ${new Date(tokenStatus.last_rotated_at).toLocaleDateString(activeLocale())}`
                : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setConfirmRotate(true)}
              disabled={tokenBusy}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {t('system:ssoScim.rotateToken')}
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => setConfirmRevoke(true)}
              disabled={tokenBusy}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {t('system:ssoScim.revoke')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('system:ssoScim.noActiveToken')}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={handleGenerate}
            disabled={tokenBusy}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {tokenBusy ? t('system:ssoScim.generating') : t('system:ssoScim.generateToken')}
          </Button>
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Group mappings */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupOutlined fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            {t('system:ssoScim.groupMappingsTitle')}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setAddOpen(true)}
          sx={{ borderRadius: 1, textTransform: 'none' }}
        >
          {t('system:ssoScim.addMapping')}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {t('system:ssoScim.groupMappingsCaption')}
      </Typography>

      {mappingsLoading ? (
        <CircularProgress size={20} />
      ) : mappings.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('system:ssoScim.noMappings')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('system:ssoScim.noMappingsHint')}
          </Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{t('system:ssoScim.tableHeaders.idpGroup')}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{t('system:ssoScim.tableHeaders.type')}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{t('system:ssoScim.tableHeaders.target')}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }} align="right">
                {t('system:ssoScim.tableHeaders.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((m) => (
              <TableRow key={m.id}>
                <TableCell>
                  <Typography variant="body2">
                    {m.idp_group_display_name || m.idp_group_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {m.idp_group_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={m.target_kind}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {m.target_display_name || m.target_id}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => setPendingDeleteMapping(m)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Show-once token modal */}
      <Dialog open={showOnce} onClose={() => setShowOnce(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('system:ssoScim.showOnceTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('system:ssoScim.showOnceBody')}
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              wordBreak: 'break-all',
              mb: 1,
            }}
          >
            {generated?.token}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyToken}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('system:ssoScim.copy')}
          </Button>
          <Button
            onClick={() => {
              setShowOnce(false);
              setGenerated(null);
              fetchToken();
            }}
            variant="contained"
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('system:ssoScim.savedConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add mapping dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('system:ssoScim.addMappingTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('system:ssoScim.form.idpGroupIdLabel')}
              size="small"
              fullWidth
              required
              placeholder={t('system:ssoScim.form.idpGroupIdPlaceholder')}
              value={addForm.idp_group_id}
              onChange={(e) => setAddForm({ ...addForm, idp_group_id: e.target.value })}
              helperText={t('system:ssoScim.form.idpGroupIdHelper')}
            />
            <TextField
              label={t('system:ssoScim.form.displayNameLabel')}
              size="small"
              fullWidth
              placeholder={t('system:ssoScim.form.displayNamePlaceholder')}
              value={addForm.idp_group_display_name ?? ''}
              onChange={(e) => setAddForm({ ...addForm, idp_group_display_name: e.target.value })}
              helperText={t('system:ssoScim.form.displayNameHelper')}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t('system:ssoScim.form.mapToLabel')}</InputLabel>
              <Select
                label={t('system:ssoScim.form.mapToLabel')}
                value={addForm.target_kind}
                onChange={(e) => setAddForm({
                  ...addForm,
                  target_kind: e.target.value as 'department' | 'role',
                  target_id: '',
                })}
              >
                <MenuItem value="department">{t('system:ssoScim.form.targetDepartment')}</MenuItem>
                <MenuItem value="role">{t('system:ssoScim.form.targetRole')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>
                {addForm.target_kind === 'department' ? t('system:ssoScim.form.departmentLabel') : t('system:ssoScim.form.roleLabel')}
              </InputLabel>
              <Select
                label={addForm.target_kind === 'department' ? t('system:ssoScim.form.departmentLabel') : t('system:ssoScim.form.roleLabel')}
                value={addForm.target_id}
                onChange={(e) => setAddForm({ ...addForm, target_id: e.target.value as string })}
              >
                {(addForm.target_kind === 'department' ? departments : roles).map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} sx={{ borderRadius: 1, textTransform: 'none' }}>
            {t('system:ssoScim.cancel')}
          </Button>
          <Button
            onClick={handleAddMapping}
            variant="contained"
            disabled={addBusy || !addForm.idp_group_id || !addForm.target_id}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {addBusy ? t('system:ssoScim.saving') : t('system:ssoScim.addMapping')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmRotate}
        title={t('system:ssoScim.rotateConfirmTitle')}
        description={t('system:ssoScim.rotateConfirmDescription')}
        confirmLabel={t('system:ssoScim.rotateConfirmButton')}
        tone="warning"
        onConfirm={handleGenerate}
        onClose={() => setConfirmRotate(false)}
      />

      <ConfirmDialog
        open={confirmRevoke}
        title={t('system:ssoScim.revokeConfirmTitle')}
        description={t('system:ssoScim.revokeConfirmDescription')}
        confirmLabel={t('system:ssoScim.revokeConfirmButton')}
        tone="danger"
        onConfirm={handleRevoke}
        onClose={() => setConfirmRevoke(false)}
      />

      <ConfirmDialog
        open={!!pendingDeleteMapping}
        title={t('system:ssoScim.deleteMappingTitle')}
        description={t('system:ssoScim.deleteMappingConfirm')}
        confirmLabel={t('common:actions.delete')}
        tone="danger"
        onConfirm={confirmDeleteMapping}
        onClose={() => setPendingDeleteMapping(null)}
      />
    </Box>
  );
};

export default SsoScimSection;
